import { Activity } from "@/graphql/query/activity/feed";
import Vue, { CreateElement, VNodeChildren } from "vue";

export const linkMap: { [k: string]: (keyof Activity)[] } = {
  "app-account-details": ["group", "appUser"],
  "building-details": ["group", "building"],
  "group-details": ["group"],
  "group-user-details": ["group", "user"],
  "room-details": ["building", "room"]
};

export const getRouterParams = (activity: Activity): object => {
  const { user, appUser, group, building, room } = activity;
  return {
    appUserId: appUser ? appUser.id : undefined,
    buildingId: building ? building.id : undefined,
    groupId: group ? group.id : undefined,
    roomId: room ? room.id : undefined,
    userId: user ? user.id : undefined,
    roomGroupId: room && room.group ? room.group.id : undefined
  };
};

export const nameLink = (
  name: string,
  routeName: string,
  obj: { name: string } | null,
  activity: Activity,
  createElement: CreateElement
): VNodeChildren[] => {
  // objが空ならテキストのみ
  if (!obj) {
    return [name];
  }

  // リンクに必要なオブジェクトが1つでも欠けていればテキストのみ
  const requiredPropNames = linkMap[routeName];
  if (!requiredPropNames.map(t => !!activity[t]).every(e => e)) {
    return [name];
  }

  // リンク付きテキスト
  return [
    name,
    "「",
    [
      createElement(
        "router-link",
        {
          props: { to: { name: routeName, params: getRouterParams(activity) } }
        },
        [obj.name]
      )
    ],
    "」"
  ];
};

type Props = { activity: Activity };
export const GroupUserLink = Vue.component<Props>("group-user-link", {
  functional: true,
  props: { activity: { type: Object, required: true } },
  render(createElement, context) {
    const user = context.props.activity.user;

    // 削除された場合はリンクしない
    if (user.is_deleted) {
      return createElement("span", `ユーザー「${user.name}(削除済み)」`);
    } else {
      return createElement(
        "span",
        nameLink(
          "ユーザー",
          "group-user-details",
          context.props.activity.user,
          context.props.activity,
          createElement
        )
      );
    }
  }
});
export const RequestGroupUserLink = Vue.component<Props>(
  "request-group-user-link",
  {
    functional: true,
    props: { activity: { type: Object, required: true } },
    render(createElement, context) {
      const { activity } = context.props;
      const reqUser = context.props.activity.requestUser;
      const code = context.props.activity.code;

      // ユーザー操作がない場合(電話カギ解施錠など)
      if (!reqUser) {
        switch (code) {
          case "assign_app_user":
          case "remove_app_user":
          case "create_group":
          case "update_group":
          case "delete_group":
          case "create_user":
          case "delete_user":
          case "update_user":
          case "reset_user":
          case "create_building":
          case "update_building":
          case "delete_building":
          case "change_building_group":
          case "create_room":
          case "update_room":
          case "delete_room":
          case "create_family_key":
          case "create_tel_key":
          case "delete_tel_key":
          case "free_room":
          case "rent_room":
          case "remote_open":
          case "remote_close":
            return createElement("span", "(親グループのユーザー)");
            break;
          case "tel_open":
          case "tel_close":
            return createElement("span", "(電話経由)");
            break;
          default:
            return createElement("span", "-");
            break;
        }
      }

      // 削除された場合はリンクしない
      if (reqUser.is_deleted) {
        return createElement("span", `ユーザー「${reqUser.name}(削除済み)」`);
      } else {
        return createElement(
          "span",
          nameLink(
            "ユーザー",
            "group-user-details",
            activity.requestUser,
            {
              ...activity,
              group: activity.requestGroup,
              user: activity.requestUser
            },
            createElement
          )
        );
      }
    }
  }
);

export const AppAccountLink = Vue.component<Props>("app-account-link", {
  functional: true,
  props: { activity: { type: Object, required: true } },
  render(createElement, context) {
    const appUser = context.props.activity.appUser;

    // グループ違いで取得できていないとき
    if (appUser === null) {
      return createElement("span", `設置アプリアカウント`);
    } else if (appUser.isDeleted === true) {
      return createElement(
        "span",
        `設置アプリアカウント「${appUser.name}(削除済み)」`
      );
    } else {
      return createElement(
        "span",
        nameLink(
          "設置アプリアカウント",
          "app-account-details",
          context.props.activity.appUser,
          context.props.activity,
          createElement
        )
      );
    }
  }
});

export const GroupLink = Vue.component<Props>("group-link", {
  functional: true,
  props: { activity: { type: Object, required: true } },
  render(createElement, context) {
    return createElement(
      "span",
      nameLink(
        "グループ",
        "group-details",
        context.props.activity.group,
        context.props.activity,
        createElement
      )
    );
  }
});

export const BuildingLink = Vue.component<Props>("building-link", {
  functional: true,
  props: { activity: { type: Object, required: true } },
  render(createElement, context) {
    return createElement(
      "span",
      nameLink(
        "建物",
        "building-details",
        context.props.activity.building,
        context.props.activity,
        createElement
      )
    );
  }
});

export const RoomLink = Vue.component<Props>("room-link", {
  functional: true,
  props: { activity: { type: Object, required: true } },
  render(createElement, context) {
    const room = context.props.activity.room;
    if (!room) {
      return createElement("span", `部屋`);
    } else if (room.isDeleted) {
      return createElement("span", `部屋「${room.name}(削除済み)」`);
    } else {
      return createElement(
        "span",
        nameLink(
          "部屋",
          "room-details",
          context.props.activity.room,
          context.props.activity,
          createElement
        )
      );
    }
  }
});
